import React from 'react';
import classNames from 'styles/utils/classNames';
import CountryFlag from 'components/CountryFlag';

import {
  formatPercentage,
  getTextColorClass,
  formatNumber,
  calculateSurprisePercentage,
  calculateChangePercentage,
} from 'screens/Calendar';
import { formatDateTimeToAMPM } from '../../utils/dateUtils';

interface EconomicEventsCardProps {
  item: any;
}

const getCountryCode = (row: any): string | null => {
  if (row.countryCode) return row.countryCode;

  const currencyToCountryCode: { [key: string]: string } = {
    USD: 'US',
    EUR: 'EU',
    GBP: 'GB',
    JPY: 'JP',
    CNY: 'CN',
    CAD: 'CA',
    AUD: 'AU',
    CHF: 'CH',
    SEK: 'SE',
    NOK: 'NO',
    // Add more mappings as needed
  };

  if (row.currency && currencyToCountryCode[row.currency]) {
    return currencyToCountryCode[row.currency];
  }

  return null;
};

export const EconomicEventsCard: React.FC<EconomicEventsCardProps> = ({ item }) => {
  const calculatedChange =
    item.actual != null && item.previous != null ? item.actual - item.previous : null;

  const calculatedChangePercentage = calculateChangePercentage(item.actual, item.previous);

  const calculatedSurprise =
    item.actual != null && item.estimate != null ? item.actual - item.estimate : null;

  const calculatedSurprisePercentage = calculateSurprisePercentage(item.actual, item.estimate);
  const countryCode = getCountryCode(item);

  const formatCombinedChange = () => {
    if (calculatedChange === null && calculatedChangePercentage === null) return '--';

    const changePart = calculatedChange !== null ? formatNumber(calculatedChange, 1) : '--';
    const percentagePart =
      calculatedChangePercentage !== null
        ? ` (${formatPercentage(calculatedChangePercentage, 1)})`
        : '';

    return `${changePart}${percentagePart}`;
  };

  return (
    <div className="rounded-lg border border-palette-border bg-palette-black-2 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-start gap-2">
          {countryCode && (
            <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg border border-gray-600">
              <CountryFlag
                code={countryCode}
                className="h-full w-full object-cover"
                fallback={<span className="text-xs">🏳️</span>}
              />
            </div>
          )}
          <div>
            <div className="text-sm font-semibold text-white">{item.country}</div>
            <div className="text-xs text-gray-400">{item.currency}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-400">Event Date</div>
          <div className="text-xs text-white">{formatDateTimeToAMPM(item.date, item.time)}</div>
        </div>
      </div>

      <div className="py-2">
        <div className="grid grid-cols-[3fr_1fr] gap-2 text-sm">
          <div className="min-w-0">
            <span className="mb-1 block text-xs text-gray-400">Event</span>
            <span className="break-word block text-xs">{item.event}</span>
          </div>
          <div className="text-right">
            <span className="mb-1 block text-xs text-gray-400">Impact</span>
            <span
              className={classNames(
                'rounded px-2 py-1 text-xs',
                item.impact === 'High'
                  ? 'bg-red-500/10 text-red-500'
                  : item.impact === 'Medium'
                  ? 'bg-yellow-200/10 text-yellow-200'
                  : item.impact === 'Low'
                  ? 'bg-green-500/10 text-green-500'
                  : 'text-white',
              )}
            >
              {item.impact || 'Medium'}
            </span>
          </div>
        </div>
      </div>
      <div className="py-2">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="min-w-0">
            <span className="mb-1 block text-xs text-gray-400">Previous</span>
            <div className="text-xs text-white">{item.previous ?? '--'}</div>
          </div>
          <div className="text-right">
            <span className="mb-1 block text-xs text-gray-400">Forecast</span>
            <div className="text-xs text-white">{item.estimate || '--'}</div>
          </div>
        </div>
      </div>
      <div className="py-2">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="min-w-0">
            <span className="mb-1 block text-xs text-gray-400">Actual</span>
            <div className="text-xs text-white">{item.actual || '--'}</div>
          </div>
          <div className="text-right">
            <span className="mb-1 block text-xs text-gray-400">Change (%)</span>
            <div
              className={classNames(
                'text-xs',
                calculatedChange !== null ? getTextColorClass(calculatedChange) : 'text-white',
              )}
            >
              {formatCombinedChange()}
            </div>
          </div>
        </div>
      </div>

      {(calculatedSurprise !== null || calculatedSurprisePercentage !== null) && (
        <div className="py-2">
          <div className="grid grid-cols-2 gap-2 text-xs">
            {calculatedSurprise !== null && (
              <div>
                <div className="mb-1 text-xs text-gray-400">Surprise</div>
                <div className={classNames('text-xs', getTextColorClass(calculatedSurprise))}>
                  {calculatedSurprise > 0 ? '+' : ''}
                  {calculatedSurprise.toFixed(2)}
                </div>
              </div>
            )}
            {calculatedSurprisePercentage !== null && (
              <div className="text-right">
                <div className="mb-1 text-xs text-gray-400">Surprise %</div>
                <div
                  className={classNames('text-xs', getTextColorClass(calculatedSurprisePercentage))}
                >
                  {formatPercentage(calculatedSurprisePercentage, 2)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
